import {
  Announcement,
  Checklist,
  CreateChecklistData,
  CreateExpenseData,
  CreateTripData,
  DailyPlan,
  Document,
  Expense,
  Location,
  Notification,
  Trip,
  TripMember,
  UpdateTripData,
} from '../../types';
import { mockTrips } from './trips.mock';

interface TripDetailStore {
  checklists: Checklist[];
  expenses: Expense[];
  members: TripMember[];
  announcements: Announcement[];
  locations: Location[];
  notifications: Notification[];
  dailyPlans: DailyPlan[];
  documents: Document[];
}

const now = () => new Date().toISOString();

function seedStore(tripId: string): TripDetailStore {
  const isNorth = tripId === 'mock-trip-2';
  return {
    checklists: [
      {
        id: `${tripId}-cl-1`,
        tripId,
        title: isNorth ? 'رزرو ویلا' : 'رزرو بلیط پرواز',
        description: isNorth ? 'تماس با میزبان' : 'خرید بلیط رفت و برگشت',
        isCompleted: isNorth,
        order: 0,
        createdAt: now(),
      },
      {
        id: `${tripId}-cl-2`,
        tripId,
        title: isNorth ? 'خرید خواربار' : 'رزرو هتل',
        isCompleted: false,
        order: 1,
        createdAt: now(),
      },
    ],
    expenses: [
      {
        id: `${tripId}-ex-1`,
        tripId,
        description: isNorth ? 'بنزین' : 'ویزا',
        amount: isNorth ? 850000 : 3200000,
        paidBy: '1',
        createdAt: now(),
        updatedAt: now(),
      },
    ],
    members: [
      {
        id: `${tripId}-m-1`,
        userId: '1',
        userName: 'کاربر تست',
        phoneNumber: '09132655299',
        role: 'owner',
        isActive: true,
      },
    ],
    announcements: [
      {
        id: `${tripId}-an-1`,
        title: 'اطلاعیه سفر',
        content: isNorth ? 'ساعت حرکت جمعه ۶ صبح' : 'پاسپورت را همراه داشته باشید',
        priority: 'medium',
        createdAt: now(),
        createdBy: '1',
      },
    ],
    locations: [
      {
        id: `${tripId}-loc-1`,
        name: isNorth ? 'ویلا ساحلی' : 'فرودگاه امام خمینی',
        address: isNorth ? 'نوشهر، ساحل' : 'تهران',
        latitude: isNorth ? 36.65 : 35.6892,
        longitude: isNorth ? 51.42 : 51.389,
      },
    ],
    notifications: [],
    dailyPlans: [
      {
        id: `${tripId}-dp-1`,
        dayNumber: 1,
        date: isNorth ? '2026-07-20' : '2026-08-01',
        activities: isNorth ? ['حرکت از تهران', 'ورود به ویلا'] : ['پرواز', 'ورود به هتل'],
      },
    ],
    documents: [],
  };
}

const tripDetailsById: Record<string, TripDetailStore> = {
  'mock-trip-1': seedStore('mock-trip-1'),
  'mock-trip-2': seedStore('mock-trip-2'),
};

function ensureTripStore(tripId: string): TripDetailStore {
  if (!tripDetailsById[tripId]) {
    tripDetailsById[tripId] = seedStore(tripId);
  }
  return tripDetailsById[tripId];
}

export function getMockTripById(tripId: string): Promise<Trip> {
  let trip = mockTrips.find((t) => t.id === tripId);
  if (!trip) {
    if (tripId === '1' || tripId === 'mock-trip-1') trip = mockTrips[0];
    else if (tripId === '2' || tripId === 'mock-trip-2') trip = mockTrips[1];
    else if (tripId === '3') trip = mockTrips[2];
    else if (tripId === '4') trip = mockTrips[3];
    else if (tripId === '5') trip = mockTrips[4];
    else trip = mockTrips[0];
  }
  return Promise.resolve({ ...trip, id: tripId });
}

export function createMockTrip(data: CreateTripData): Promise<Trip> {
  const trip: Trip = {
    id: `mock-trip-${Date.now()}`,
    title: data.title,
    origin: data.origin,
    destination: data.destination,
    description: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    visibility: data.visibility ?? 'private',
    status: data.status ?? 'planning',
    createdAt: now(),
    updatedAt: now(),
  };
  mockTrips.push(trip);
  tripDetailsById[trip.id] = seedStore(trip.id);
  return Promise.resolve(trip);
}

export function updateMockTrip(tripId: string, data: UpdateTripData): Promise<Trip> {
  const index = mockTrips.findIndex((t) => t.id === tripId);
  if (index < 0) return Promise.reject(new Error('سفر یافت نشد'));
  mockTrips[index] = { ...mockTrips[index], ...data, updatedAt: now() };
  return Promise.resolve(mockTrips[index]);
}

export function deleteMockTrip(tripId: string): Promise<void> {
  const index = mockTrips.findIndex((t) => t.id === tripId);
  if (index >= 0) mockTrips.splice(index, 1);
  delete tripDetailsById[tripId];
  return Promise.resolve();
}

export function getMockChecklists(tripId: string): Promise<Checklist[]> {
  return Promise.resolve([...ensureTripStore(tripId).checklists]);
}

export function createMockChecklist(tripId: string, data: CreateChecklistData): Promise<Checklist> {
  const store = ensureTripStore(tripId);
  const item: Checklist = {
    id: `${tripId}-cl-${Date.now()}`,
    tripId,
    title: data.title,
    description: data.description,
    isCompleted: false,
    order: store.checklists.length,
    createdAt: now(),
  };
  store.checklists.push(item);
  return Promise.resolve(item);
}

export function toggleMockChecklist(tripId: string, checklistId: string, isCompleted: boolean): Promise<Checklist> {
  const store = ensureTripStore(tripId);
  const item = store.checklists.find((c) => c.id === checklistId);
  if (!item) return Promise.reject(new Error('چک‌لیست یافت نشد'));
  item.isCompleted = isCompleted;
  item.updatedAt = now();
  return Promise.resolve({ ...item });
}

export function updateMockChecklist(tripId: string, checklistId: string, data: Partial<Checklist>): Promise<Checklist> {
  const store = ensureTripStore(tripId);
  const index = store.checklists.findIndex((c) => c.id === checklistId);
  if (index < 0) return Promise.reject(new Error('چک‌لیست یافت نشد'));
  store.checklists[index] = { ...store.checklists[index], ...data, updatedAt: now() };
  return Promise.resolve(store.checklists[index]);
}

export function deleteMockChecklist(tripId: string, checklistId: string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.checklists = store.checklists.filter((c) => c.id !== checklistId);
  return Promise.resolve();
}

export function getMockExpenses(tripId: string): Promise<Expense[]> {
  return Promise.resolve([...ensureTripStore(tripId).expenses]);
}

export function createMockExpense(tripId: string, data: CreateExpenseData): Promise<Expense> {
  const expense: Expense = {
    id: `${tripId}-ex-${Date.now()}`,
    tripId,
    description: data.description,
    amount: data.amount,
    paidBy: data.payerId ?? '1',
    splitAmong: data.participantIds,
    createdAt: now(),
    updatedAt: now(),
  };
  ensureTripStore(tripId).expenses.push(expense);
  return Promise.resolve(expense);
}

export function deleteMockExpense(tripId: string, expenseId: string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.expenses = store.expenses.filter((e) => e.id !== expenseId);
  return Promise.resolve();
}

export function getMockTripMembers(tripId: string): Promise<TripMember[]> {
  return Promise.resolve([...ensureTripStore(tripId).members]);
}

export function addMockTripMember(tripId: string, data: { phoneNumber: string; role: string }): Promise<void> {
  const store = ensureTripStore(tripId);
  store.members.push({
    id: `${tripId}-m-${Date.now()}`,
    userId: `user-${Date.now()}`,
    userName: data.phoneNumber,
    phoneNumber: data.phoneNumber,
    role: data.role as TripMember['role'],
    isActive: true,
  });
  return Promise.resolve();
}

export function removeMockTripMember(tripId: string, memberId: string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.members = store.members.filter((m) => m.id !== memberId);
  return Promise.resolve();
}

export function getMockAnnouncements(tripId: string): Promise<Announcement[]> {
  return Promise.resolve([...ensureTripStore(tripId).announcements]);
}

export function createMockAnnouncement(tripId: string, content: string): Promise<Announcement> {
  const item: Announcement = {
    id: `${tripId}-an-${Date.now()}`,
    title: 'اطلاعیه',
    content,
    priority: 'medium',
    createdAt: now(),
    createdBy: '1',
  };
  ensureTripStore(tripId).announcements.push(item);
  return Promise.resolve(item);
}

export function deleteMockAnnouncement(tripId: string, announcementId: number | string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.announcements = store.announcements.filter((a) => a.id !== String(announcementId));
  return Promise.resolve();
}

export function getMockLocations(tripId: string): Promise<Location[]> {
  return Promise.resolve([...ensureTripStore(tripId).locations]);
}

export function createMockLocation(tripId: string, name: string, address: string): Promise<Location> {
  const item: Location = { id: `${tripId}-loc-${Date.now()}`, name, address };
  ensureTripStore(tripId).locations.push(item);
  return Promise.resolve(item);
}

export function deleteMockLocation(tripId: string, locationId: number | string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.locations = store.locations.filter((l) => l.id !== String(locationId));
  return Promise.resolve();
}

export function getMockTripNotifications(tripId: string): Promise<any[]> {
  return Promise.resolve([]);
}

export function getMockDailyPlans(tripId: string): Promise<DailyPlan[]> {
  return Promise.resolve([...ensureTripStore(tripId).dailyPlans]);
}

export function createMockDailyPlan(
  tripId: string,
  dayNumber: number,
  date: string,
  activities: string[]
): Promise<DailyPlan> {
  const item: DailyPlan = { id: `${tripId}-dp-${Date.now()}`, dayNumber, date, activities };
  ensureTripStore(tripId).dailyPlans.push(item);
  return Promise.resolve(item);
}

export function deleteMockDailyPlan(tripId: string, planId: number | string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.dailyPlans = store.dailyPlans.filter((p) => p.id !== String(planId));
  return Promise.resolve();
}

export function getMockDocuments(tripId: string): Promise<Document[]> {
  return Promise.resolve([...ensureTripStore(tripId).documents]);
}

export function createMockDocument(
  tripId: string,
  name: string,
  _filePath: string,
  fileType: string,
  fileSize: number,
  uploadedBy: string
): Promise<Document> {
  const item: Document = {
    id: `${tripId}-doc-${Date.now()}`,
    name,
    type: fileType,
    size: fileSize,
    url: '#',
    uploadedAt: now(),
    uploadedBy,
  };
  ensureTripStore(tripId).documents.push(item);
  return Promise.resolve(item);
}

export function deleteMockDocument(tripId: string, documentId: number | string): Promise<void> {
  const store = ensureTripStore(tripId);
  store.documents = store.documents.filter((d) => d.id !== String(documentId));
  return Promise.resolve();
}
